import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Car, ChevronRight, ChevronDown, Newspaper, ArrowUp, MessageSquare } from 'lucide-react';
import { api } from '@/lib/api';
import type { VehicleCluster } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import { outboundUrl } from '@/lib/post-origin';

// AI-003: Vehicle Entity Feed Clustering — 'All News About This Car'
// Right sidebar widget showing top vehicle entities with clustered posts.

function ClusterRow({ cluster }: { cluster: VehicleCluster }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-border-primary last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-bg-secondary"
      >
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
          <Car className="h-4 w-4 text-primary-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text-primary">{cluster.vehicle}</p>
          <p className="text-xs text-text-tertiary">
            {cluster.brand} &middot; {cluster.postCount} articles
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronRight className="h-4 w-4 text-text-tertiary" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="space-y-1 px-3 pb-2">
          {cluster.previewPosts.map((post) => (
            <a
              key={post.id}
              href={outboundUrl(post) ?? `/post/${post.id}`}
              {...(outboundUrl(post) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="group flex items-start gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-bg-tertiary"
              onClick={() => {
                try {
                  navigator.sendBeacon?.(
                    '/api/analytics/events',
                    JSON.stringify({
                      event: 'vehicle_cluster_post_click',
                      data: { vehicle: cluster.slug, postId: post.id },
                      timestamp: Date.now(),
                    })
                  );
                } catch { /* noop */ }
              }}
            >
              <Newspaper className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-text-tertiary group-hover:text-primary-500" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-xs font-medium text-text-secondary group-hover:text-text-primary">
                  {post.title}
                </p>
                <div className="mt-0.5 flex items-center gap-2 text-[10px] text-text-tertiary">
                  {post.source && <span>{post.source.name}</span>}
                  <span className="flex items-center gap-0.5">
                    <ArrowUp className="h-2.5 w-2.5" />
                    {formatNumber(post.upvoteCount)}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <MessageSquare className="h-2.5 w-2.5" />
                    {post.commentCount}
                  </span>
                </div>
              </div>
            </a>
          ))}
          {cluster.postCount > 3 && (
            <a
              href={`/?make=${encodeURIComponent(cluster.brand)}&tag=${cluster.slug}`}
              className="flex items-center justify-center gap-1 rounded-lg py-1 text-[11px] font-medium text-primary-500 hover:text-primary-400"
            >
              See all {cluster.postCount} articles
              <ChevronRight className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function VehicleClusterWidget() {
  const { data, isLoading } = useQuery({
    queryKey: ['feed', 'vehicle-clusters'],
    queryFn: () => api.feed.vehicleClusters({ limit: 6, days: 7 }),
    staleTime: 30 * 60 * 1000, // 30 min
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border-primary bg-bg-primary">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary">
          <Car className="h-4 w-4 text-primary-500" />
          <h3 className="text-sm font-bold text-text-primary">Trending Vehicles</h3>
        </div>
        <div className="space-y-3 px-3 py-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 animate-pulse rounded-lg bg-bg-tertiary" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 w-3/4 animate-pulse rounded bg-bg-tertiary" />
                <div className="h-2.5 w-1/2 animate-pulse rounded bg-bg-tertiary" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data?.items || data.items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-primary">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary">
        <Car className="h-4 w-4 text-primary-500" />
        <h3 className="text-sm font-bold text-text-primary">Trending Vehicles</h3>
        <span className="ml-auto text-[10px] font-medium text-text-tertiary">This Week</span>
      </div>
      <div>
        {data.items.map((cluster) => (
          <ClusterRow key={cluster.slug} cluster={cluster} />
        ))}
      </div>
    </div>
  );
}
